import { useSearchParams } from 'react-router-dom';
import { ManageLeave, LeaveCalendar, LeaveHistory, LeaveStatus, leaveError } from './LeaveManagement';
import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { todayIso } from "@/lib/dates";
import { PageHeader, StatusBadge, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Check, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const LABEL = {
  annual: "Annual leave",
  sick: "Sick",
  bereavement: "Bereavement",
  unpaid: "Unpaid",
  other: "Other",
};

function BalanceCard({ label, value, max = 20 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const low = value <= max * 0.2;
  return (
    <div className="hard-card p-4">
      <div className="flex items-center justify-between">
        <span className="label-tag">{label}</span>
        <span className={`metric text-2xl font-bold ${low ? "text-red-600" : ""}`}>{value.toFixed(1)}</span>
      </div>
      <div className="mt-2 h-1 bg-zinc-200 overflow-hidden">
        <div className={`h-full ${low ? "bg-red-600" : "bg-[#FF5F15]"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] font-mono text-zinc-500 mt-1.5">days remaining</div>
    </div>
  );
}

export default function Leave() {
  const { user } = useAuth();
  const isOffice = ["admin", "office", "super_admin"].includes(user.role);
  const [searchParams] = useSearchParams();
  const selectedWorker = isOffice ? searchParams.get('worker') : null;
  const [manageId, setManageId] = useState(() => isOffice ? searchParams.get('record') : null);
  const [view, setView] = useState("list");
  const [calendarVersion, setCalendarVersion] = useState(0);
  const [approvalConflict, setApprovalConflict] = useState(null);
  const [items, setItems] = useState([]);
  const [balances, setBalances] = useState({ annual: 0, sick: 0, bereavement: 0 });
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/leave", { params: selectedWorker ? { user_id: selectedWorker } : undefined });
      setItems(data);
      const b = await api.get("/leave/balances/me");
      setBalances(b.data || {});
    } catch (e) { toast.error(leaveError(e)); } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, [selectedWorker]); // eslint-disable-line react-hooks/exhaustive-deps

  const review = async (id, status, acknowledge_conflicts = false) => {
    try {
      await api.patch(`/leave/${id}`, { status, acknowledge_conflicts });
      setApprovalConflict(null); toast.success(`Request ${status}`); load(); setCalendarVersion(v=>v+1);
    } catch(e) {
      if(e.response?.data?.detail?.conflicts) setApprovalConflict({id,status,...e.response.data.detail});
      else toast.error(leaveError(e));
    }
  };

  return (
    <div data-testid="leave-page" className="px-4 py-4 md:p-6 lg:p-10 md:max-w-7xl md:mx-auto pb-24 md:pb-10">
      <PageHeader
        tag="/ Time off"
        title="Leave"
        subtitle={isOffice ? (selectedWorker ? "Leave requests for the selected employee." : "Review and approve leave requests.") : "Request annual, sick or other leave."}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-leave-button">
                <Plus className="w-4 h-4 mr-2" /> Request leave
              </Button>
            </DialogTrigger>
            <LeaveForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />

      <div className="flex gap-2 mb-4"><Button variant={view==='list'?'default':'outline'} onClick={()=>setView('list')}>List</Button><Button variant={view==='calendar'?'default':'outline'} onClick={()=>setView('calendar')}>Calendar</Button></div>
      {view === 'calendar' ? <LeaveCalendar key={calendarVersion} userId={selectedWorker} canManage={isOffice} onManage={setManageId}/> : !loaded ? (
        <div className="space-y-3" data-testid="leave-loading">
          {[0, 1, 2].map((i) => (<div key={i} className="h-24 border-2 border-zinc-200 bg-zinc-50 animate-pulse" />))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="No leave requests yet" description="Submit one to get started." />
      ) : (
        <div className="hard-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 dark:bg-zinc-800 border-b border-zinc-300 text-left">
              <tr>
                {isOffice && <Th>Worker</Th>}
                <Th>Type</Th>
                <Th>Dates</Th>
                <Th>Reason</Th>
                <Th>Status</Th>
                {isOffice && <Th className="text-right">Actions</Th>}
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const isOpen = !!expanded[l.id];
                return (
                  <Fragment key={l.id}>
                    <tr className="border-b border-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [l.id]: !s[l.id] }))} data-testid={`leave-row-${l.id}`}>
                      {isOffice && <Td>{l.user_name}</Td>}
                      <Td>
                        <div className="flex items-center gap-2">
                          <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          {LABEL[l.leave_type]}
                        </div>
                      </Td>
                      <Td className="font-mono text-xs">{l.start_date} → {l.end_date}<br/><span className="text-zinc-400">{l.days ?? 0} day{l.days === 1 ? "" : "s"}</span></Td>
                      <Td className="text-zinc-600 max-w-xs truncate">{l.reason || "—"}</Td>
                      <Td><LeaveStatus status={l.status} partial={!!l.remaining_request_ids?.length} /></Td>
                      {isOffice && (
                        <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" onClick={()=>setManageId(l.id)} className="mb-1">Manage</Button>
                          {l.status === "pending" && (
                            <div className="flex justify-end gap-1">
                              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm border-green-500 text-green-600 hover:bg-green-50" onClick={() => review(l.id, "approved")} data-testid={`approve-leave-${l.id}`}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm border-red-500 text-red-600 hover:bg-red-50" onClick={() => review(l.id, "rejected")} data-testid={`reject-leave-${l.id}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </Td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr className="bg-zinc-50 dark:bg-zinc-900 border-b border-zinc-200" data-testid={`leave-detail-${l.id}`}>
                        <td colSpan={isOffice ? 6 : 5} className="px-4 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <DetailCol label="Worker" value={l.user_name} />
                            <DetailCol label="Type" value={LABEL[l.leave_type]} />
                            <DetailCol label="Working days" value={`${l.days ?? 0}`} />
                            <DetailCol label="Submitted" value={new Date(l.created_at).toLocaleString("en-NZ")} mono />
                          </div>
                          {l.reason && (
                            <div className="mt-4">
                              <div className="label-tag mb-1">Reason</div>
                              <p className="text-sm text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap leading-relaxed">{l.reason}</p>
                            </div>
                          )}
                          <LeaveHistory request={l}/>
                          {l.parent_id && <p className="mt-2 text-xs">Remaining portion of request {l.parent_id}</p>}
                          {(l.review_notes || l.reviewed_at) && (
                            <div className="mt-4 border-l-2 border-zinc-300 pl-3">
                              <div className="label-tag mb-1">Review</div>
                              {l.reviewed_at && <div className="text-xs text-zinc-500 font-mono">Reviewed {new Date(l.reviewed_at).toLocaleString("en-NZ")}</div>}
                              {l.review_notes && <p className="text-sm text-zinc-800 dark:text-zinc-200 mt-1">{l.review_notes}</p>}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {manageId && <ManageLeave id={manageId} onClose={()=>setManageId(null)} onSaved={()=>{load();setCalendarVersion(v=>v+1);}}/>}
      {approvalConflict && <Dialog open onOpenChange={v=>{if(!v)setApprovalConflict(null);}}><DialogContent><DialogHeader><DialogTitle>Leave overlaps crew allocations</DialogTitle><DialogDescription>Approval will retain the existing crew allocations.</DialogDescription></DialogHeader>{approvalConflict.conflicts.map(c=><p key={c.id}>{c.date} · {c.job_title || c.job_id}</p>)}<Button onClick={()=>review(approvalConflict.id,approvalConflict.status,true)}>Approve and retain allocations</Button></DialogContent></Dialog>}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "", onClick }) {
  return <td className={`px-4 py-3 align-middle ${className}`} onClick={onClick}>{children}</td>;
}
function DetailCol({ label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      <div className={`${mono ? "font-mono text-xs" : ""} text-zinc-800 dark:text-zinc-200`}>{value}</div>
    </div>
  );
}

function LeaveForm({ onDone }) {
  const today = todayIso();
  const [form, setForm] = useState({ leave_type: "annual", start_date: today, end_date: today, reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leave", form);
      toast.success("Leave requested");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">Request leave</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Type</Label>
          <Select value={form.leave_type} onValueChange={set("leave_type")}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Start</Label>
            <Input required type="date" value={form.start_date} onChange={set("start_date")} className="mt-2 rounded-sm border-zinc-400" data-testid="leave-start-input" />
          </div>
          <div>
            <Label className="label-tag">End</Label>
            <Input required type="date" value={form.end_date} onChange={set("end_date")} className="mt-2 rounded-sm border-zinc-400" data-testid="leave-end-input" />
          </div>
        </div>
        <div>
          <Label className="label-tag">Reason</Label>
          <Textarea rows={3} value={form.reason} onChange={set("reason")} className="mt-2 rounded-sm border-zinc-400" placeholder="Optional" data-testid="leave-reason-input" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="leave-submit-button">
            {busy ? "Submitting..." : "Submit request"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
